import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Stethoscope,
  Check,
  X,
  Loader2,
  Phone,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const AdminValidations = () => {
  const [pendingProfessionals, setPendingProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPendingProfessionals();
  }, []);

  const loadPendingProfessionals = async () => {
    try {
      const response = await adminAPI.getPendingProfessionals();
      setPendingProfessionals(response.data.professionals);
    } catch (error) {
      console.error('Error loading pending professionals:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async (profileId, approved) => {
    try {
      await adminAPI.validateProfessional(profileId, approved);
      toast.success(approved ? 'Professionnel validé avec succès!' : 'Professionnel rejeté');
      loadPendingProfessionals();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="admin-validations-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Validations
        </h1>
        <p className="text-slate-600 mt-1">
          {pendingProfessionals.length} professionnel(s) en attente de validation
        </p>
      </div>

      {pendingProfessionals.length > 0 ? (
        <div className="space-y-4">
          {pendingProfessionals.map((prof) => (
            <Card key={prof.id} className="border-0 shadow-sm" data-testid={`pending-professional-${prof.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-8 h-8 text-red-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        Dr. {prof.user?.full_name}
                      </h3>
                      <Badge className="mt-1 bg-red-100 text-red-600">
                        {prof.specialty}
                      </Badge>
                      
                      <div className="mt-3 space-y-1 text-sm text-slate-600">
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {prof.user?.phone}
                        </p>
                        {prof.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {prof.address}
                          </p>
                        )}
                      </div>
                      
                      {prof.license_number && (
                        <p className="mt-2 text-sm">
                          <span className="text-slate-500">Licence:</span>{' '}
                          <span className="font-medium">{prof.license_number}</span>
                        </p>
                      )}
                      
                      {prof.description && (
                        <p className="mt-2 text-sm text-slate-500 max-w-xl">
                          {prof.description}
                        </p>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        {prof.services?.map((service, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {service === 'consultation' ? 'Consultation' : 
                             service === 'video_call' ? 'Téléconsultation' : 
                             'Domicile'}
                          </Badge>
                        ))}
                      </div>

                      <div className="mt-3 flex gap-4 text-sm">
                        {prof.consultation_fee > 0 && (
                          <span>Consultation: <strong>{prof.consultation_fee?.toLocaleString()} FCFA</strong></span>
                        )}
                        {prof.video_fee > 0 && (
                          <span>Vidéo: <strong>{prof.video_fee?.toLocaleString()} FCFA</strong></span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 md:flex-col">
                    <Button
                      onClick={() => handleValidation(prof.id, false)}
                      variant="outline"
                      className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`reject-${prof.id}`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Rejeter
                    </Button>
                    <Button
                      onClick={() => handleValidation(prof.id, true)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`approve-${prof.id}`}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Valider
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">Aucune validation en attente</h3>
            <p className="text-slate-500 mt-2">
              Tous les professionnels ont été validés
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminValidations;
